import React, { useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useAssignableCompaniesQuery } from 'components/hooks/assignableCompanyQueries';
import { useAssignableCompanyRolesQuery } from 'components/hooks/assignableCompanyRoleQueries';
import { useCreatePersonnelMutation } from 'components/hooks/personnelMutations';
import useForm from 'components/hooks/useForm';
import usePersonnelForm from 'components/hooks/usePersonnelForm';
import useProfilePhotoUploading from 'components/hooks/useProfilePhotoUploading';
import useRequestError from 'components/hooks/useRequestError';

import SidePanel from 'components/application/SidePanel';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import PersonnelForm from 'components/personnel/PersonnelForm';
import ProfilePhotoModal from 'components/personnel/ProfilePhotoModal';
import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';
import FormFooter from 'components/sidepanels/FormFooter';

import {
  personnelParams,
  sortRoleIdsByPosition,
} from 'components/helpers/personnel';
import { focusField } from 'components/helpers/refs';

const defaultPersonnel = {
  company: {
    companyId: '',
    subcontractorId: '',
  },
  companyRoleIds: [],
  email: '',
  externalId: '',
  fieldValues: {},
  firstName: '',
  id: '',
  lastName: '',
  lineManagerId: '',
  primaryCompanyRoleId: '',
  profileAccessEnabled: false,
  profilePhoto: null,
  teamSelections: [],
};

const defaultSubcontractor = { subcontractorName: '' };

export default function AddPersonnelSidePanel({
  defaultNameValue,
  onClose,
  onSuccess,
}) {
  const breadBoard = useBreadBoard();
  const queryClient = useQueryClient();

  const {
    division: { id: currentDivisionId },
  } = useCurrentActor();

  const [currentSubcontractor, onSubcontractorSelect] =
    useForm(defaultSubcontractor);

  const [newCompanyRoles, setNewCompanyRoles] = useForm([]);

  let firstName = '';
  let lastName = '';

  (function setDefaultFirstAndLastName() {
    firstName = defaultNameValue;

    const lastIndexOfSpace = defaultNameValue.lastIndexOf(' ');

    if (lastIndexOfSpace !== -1) {
      firstName = defaultNameValue.substr(0, lastIndexOfSpace);
      lastName = defaultNameValue.substr(lastIndexOfSpace + 1);
    }
  })();

  const [
    currentPersonnel,
    setCurrentPersonnel,
    handlePersonnelInputChange,
    handlePersonnelOptionChange,
    _handlePersonnelDateChange,
    handleFieldValueInputChange,
    handleDateFieldChange,
    handleFieldOptionChange,
    handleToggle,
    handleProfilePhotoRemoval,
    handleProfilePhotoMediaChange,
    handleNewProfilePhotoUploaded,
    handleProfilePhotoCropReady,
  ] = usePersonnelForm({
    ...defaultPersonnel,
    company: { ...defaultPersonnel.company, companyId: currentDivisionId },
    firstName,
    lastName,
  });

  const {
    personnelProfileAccessPolicy,
    projectId,
    projectPersonnelNestedCompanyRolePolicy,
    teamPersonnelPolicy,
  } = useProjectPersonnelContext();

  const sidePanelFieldRef = useRef();

  const [isProfilePhotoModalOpen, setIsProfilePhotoModalOpen] = useState(false);
  const [isSubcontractor, setIsSubcontractor] = useState(false);
  const [scrolledAmount, setScrolledAmount] = useState(0);
  const [uploadedProfilePhoto, setUploadedProfilePhoto] = useState(null);

  const [
    requestError,
    hasHighlightedErrors,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const {
    isProfilePhotoUploading,
    processProfilePhoto,
    resetProfilePhotoUploadState,
  } = useProfilePhotoUploading({
    getCurrentProfilePhoto: () => currentPersonnel.profilePhoto,
  });

  const { t: i18nTPersonnel } = useTranslation('common', {
    keyPrefix: 'personnel',
  });

  const {
    data: domainAssignableCompanies,
    isSuccess: isDomainAssignableCompaniesSuccess,
  } = useAssignableCompaniesQuery();

  const { data: domainAssignableCompanyRoles } =
    useAssignableCompanyRolesQuery();

  const createPersonnelMutation = useCreatePersonnelMutation();

  const handleSubmit = () => {
    createPersonnelMutation.mutate(
      {
        params: personnelParams({
          currentPersonnel,
          currentSubcontractor,
          isSubcontractor,
          managedTeamIds: currentPersonnel.teamSelections
            .filter((team) => team.isManager === true)
            .map((team) => team.id),
          newCompanyRoles,
          profilePhotos: { domain: uploadedProfilePhoto },
          teamIds: currentPersonnel.teamSelections.map((team) => team.id),
        }),
      },
      {
        onError: handleRequestError,
        onSuccess: (response) => {
          const {
            data: {
              id: personnelId,
              relationships: {
                companyRoles: { data: companyRolesData } = { data: [] },
                teams: { data: teamsData } = { data: [] },
              },
            },
          } = response;

          onSuccess({
            personnelId,
            roleIds: companyRolesData.map((companyRole) => companyRole.id),
            teamIds: teamsData.map((team) => team.id),
          });

          queryClient.invalidateQueries({
            queryKey: [
              'projects',
              projectId,
              'assignablePersonnelSearches',
              { search: defaultNameValue },
            ],
          });

          handleClose();
          resetRequestError();
        },
      },
    );
  };

  const handleClose = () => {
    handlePhotoModalClose();
    onSubcontractorSelect(defaultSubcontractor);

    setCurrentPersonnel({
      ...defaultPersonnel,
      company: { ...defaultPersonnel.company, companyId: currentDivisionId },
    });

    setIsSubcontractor(false);
    setNewCompanyRoles([]);
    setUploadedProfilePhoto(null);

    onClose();
  };

  const handleNewCompanyRoleAdd = (newRole) => {
    setNewCompanyRoles(newCompanyRoles.concat([newRole]));
  };

  const handleCompanyRolesOptionChange = (values) => {
    if (
      currentPersonnel.primaryCompanyRoleId === '' &&
      currentPersonnel.companyRoleIds.length === 0
    ) {
      setCurrentPersonnel({
        ...currentPersonnel,
        primaryCompanyRoleId: values[0].value,
        companyRoleIds: values.map((v) => v.value),
      });
    } else {
      setCurrentPersonnel({
        ...currentPersonnel,
        companyRoleIds: values.map((v) => v.value),
      });
    }
  };

  function handleSelectPrimary(id) {
    setCurrentPersonnel({ ...currentPersonnel, primaryCompanyRoleId: id });
  }

  function handleRemoveRole(id) {
    const filteredCompanyRoleIds = currentPersonnel.companyRoleIds.filter(
      (roleId) => roleId !== id,
    );

    const newPrimaryCompanyRoleId =
      filteredCompanyRoleIds.length > 0 ?
        sortRoleIdsByPosition(
          domainAssignableCompanyRoles.data,
          filteredCompanyRoleIds,
        )[0]
      : '';

    setCurrentPersonnel({
      ...currentPersonnel,
      companyRoleIds: filteredCompanyRoleIds,
      primaryCompanyRoleId:
        currentPersonnel.primaryCompanyRoleId === id ?
          newPrimaryCompanyRoleId
        : currentPersonnel.primaryCompanyRoleId,
    });
  }

  function handleTeamSelect(team) {
    const newTeamSelection = {
      id: team.id,
      isManager: false,
      persisted: false,
    };

    setCurrentPersonnel({
      ...currentPersonnel,
      teamSelections: [newTeamSelection, ...currentPersonnel.teamSelections],
    });
  }

  function handleTeamSelectionRoleChange({ isManager, teamSelection }) {
    setCurrentPersonnel({
      ...currentPersonnel,
      teamSelections: currentPersonnel.teamSelections.map((t) =>
        t.id === teamSelection.id ? { ...t, isManager } : t,
      ),
    });
  }

  function handleTeamSelectionDelete(teamId) {
    setCurrentPersonnel({
      ...currentPersonnel,
      teamSelections: currentPersonnel.teamSelections.filter(
        (teamSelection) => teamSelection.id !== teamId,
      ),
    });
  }

  function handleDivisionOptionChange(selected) {
    setCurrentPersonnel({
      ...currentPersonnel,
      company: {
        companyId: selected.value.companyId,
        subcontractorId: '',
      },
      teamSelections: [],
    });
  }

  const handlePhotoModalClose = () => {
    setIsProfilePhotoModalOpen(false);
  };

  const handlePhotoRemoved = () => {
    handleProfilePhotoRemoval();
    setUploadedProfilePhoto(null);
    resetProfilePhotoUploadState();
  };

  const handleProcessProfilePhoto = (tempPhotoFile) => {
    processProfilePhoto(tempPhotoFile, {
      onSuccess: (profilePhotoData) => {
        setUploadedProfilePhoto(profilePhotoData);
        handleNewProfilePhotoUploaded(profilePhotoData);
      },
      onError: (error) => {
        const errorMessage = error?.message || 'Profile photo upload failed';
        breadBoard.addInedibleToast({ fullMessage: errorMessage });
      },
    });
  };

  const assignableRoles = (domainAssignableCompanyRoles?.data || []).concat(
    newCompanyRoles.map((role) => ({
      id: role.id,
      attributes: { position: role.position },
      type: 'companyRole',
    })),
  );

  const isSubmitDisabled = hasHighlightedErrors || isProfilePhotoUploading;

  return (
    isDomainAssignableCompaniesSuccess && (
      <>
        <SidePanel
          bodyContent={
            <PersonnelForm
              assignableCompanies={domainAssignableCompanies.data}
              assignableRoles={assignableRoles}
              currentPersonnel={currentPersonnel}
              currentSubcontractor={currentSubcontractor}
              domainProfilePhoto={uploadedProfilePhoto}
              hasTeamPersonnelEditableAccess={teamPersonnelPolicy.canWrite}
              isCreatableCompanyRole={
                projectPersonnelNestedCompanyRolePolicy.canCreate
              }
              isEditableProfileAccess={personnelProfileAccessPolicy.canUpdate}
              isProfilePhotoLoading={isProfilePhotoUploading}
              isSubcontractor={isSubcontractor}
              onAssignableRoleAdd={() => {}}
              onCompanyRolesOptionChange={handleCompanyRolesOptionChange}
              onDateFieldChange={handleDateFieldChange}
              onDivisionOptionChange={handleDivisionOptionChange}
              onFieldOptionChange={handleFieldOptionChange}
              onNewCompanyRoleAdd={handleNewCompanyRoleAdd}
              onPersonnelFieldChange={handleFieldValueInputChange}
              onPersonnelInputChange={handlePersonnelInputChange}
              onPersonnelOptionChange={handlePersonnelOptionChange}
              onPhotoModalOpen={() => setIsProfilePhotoModalOpen(true)}
              onRemoveRole={handleRemoveRole}
              onSelectPrimary={handleSelectPrimary}
              onSubcontractorSelect={onSubcontractorSelect}
              onTeamSelect={handleTeamSelect}
              onTeamSelectionDelete={handleTeamSelectionDelete}
              onTeamSelectionRoleChange={handleTeamSelectionRoleChange}
              onToggle={handleToggle}
              removeErrorStyling={removeErrorStyling}
              requestError={requestError}
              setCurrentPersonnel={setCurrentPersonnel}
              setIsSubcontractor={setIsSubcontractor}
              sidePanelContext='new'
              sidePanelFieldRef={sidePanelFieldRef}
            />
          }
          closeCallback={handleClose}
          color='tertiary'
          contentContext='new'
          footerContent={
            <FormFooter
              onCancel={handleClose}
              onSubmit={handleSubmit}
              submitButtonDisabled={isSubmitDisabled}
              text={i18nTPersonnel('addPersonnel')}
            />
          }
          headerContent={
            <div className='popup__title popup__title--tertiary'>
              <h1 className='tw-text-xl tw-font-semibold tw-tracking-tight'>
                {i18nTPersonnel('addNewPersonnel')}
              </h1>
            </div>
          }
          isOpen={true}
          onOpen={() => focusField(sidePanelFieldRef)}
          scrolledAmount={scrolledAmount}
          setScrolledAmount={setScrolledAmount}
          sidePanelClassName='tw-z-[10000]'
          submitDisabled={isSubmitDisabled}
        />
        {isProfilePhotoModalOpen && (
          <ProfilePhotoModal
            currentPersonnel={currentPersonnel}
            domainProfilePhoto={uploadedProfilePhoto}
            key={currentPersonnel?.profilePhoto?.id || 'new-personnel-photo'}
            onCropReady={handleProfilePhotoCropReady}
            onMediaLoaded={handleProfilePhotoMediaChange}
            onPhotoModalClose={handlePhotoModalClose}
            onPhotoRemoved={handlePhotoRemoved}
            onProcessProfilePhoto={(photoFile) => {
              handlePhotoModalClose();

              if (photoFile) {
                handleProcessProfilePhoto(photoFile);
              }
            }}
          />
        )}
      </>
    )
  );
}

AddPersonnelSidePanel.propTypes = {
  defaultNameValue: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
